import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { CategoryRow } from "../../components/CategoryRow/CategoryRow";
import {
  categoryMatches,
  filterCategoriesBySearch,
  findUniqueCategoryBySearchQuery,
} from "./filterCategories";
import { useRealCatalog } from "../../hooks/useRealCatalog";
import "features/watchlist/components/UserWatchlist.css";
import styles from "./BrowseAllPage.module.css";
import SearchHeader from "../../components/HomeLanding/HomeLandingHeader";
import { EmptySearchState } from "../../components/EmptySearchState/EmptySearchState";

const BackChevronIcon: React.FC = () => (
  <svg
    width="22"
    height="22"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2.2"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden
  >
    <path d="M15 18l-6-6 6-6" />
  </svg>
);

export function BrowseAllPage() {
  const navigate = useNavigate();
  const { categories, loading, error } = useRealCatalog();
  const [searchQuery, setSearchQuery] = useState("");
  /** After Enter on search: show one category inline (Browse all) with back to reset */
  const [focusedCategoryId, setFocusedCategoryId] = useState<string | null>(
    null
  );
  const [bannerScrolled, setBannerScrolled] = useState(false);

  useEffect(() => {
    if (!focusedCategoryId || categories.length === 0) return;
    if (!categories.some((c) => c.id === focusedCategoryId)) {
      setFocusedCategoryId(null);
    }
  }, [focusedCategoryId, categories]);

  const visibleCategories = useMemo(
    () => filterCategoriesBySearch(categories, searchQuery),
    [categories, searchQuery]
  );

  const categoriesForList = useMemo(() => {
    if (!focusedCategoryId) return visibleCategories;
    const cat = categories.find((c) => c.id === focusedCategoryId);
    if (!cat) return [];
    return filterCategoriesBySearch([cat], searchQuery);
  }, [focusedCategoryId, categories, searchQuery, visibleCategories]);

  const focusedCategory = useMemo(
    () =>
      focusedCategoryId
        ? categories.find((c) => c.id === focusedCategoryId) ?? null
        : null,
    [categories, focusedCategoryId]
  );

  const exitCategoryFocus = useCallback(() => {
    setFocusedCategoryId(null);
    setSearchQuery("");
  }, []);

  const clearSearch = useCallback(() => {
    setSearchQuery("");
    setFocusedCategoryId(null);
  }, []);

  const submitSearchEnter = useCallback(() => {
    const t = searchQuery.trim();
    if (!t) return;

    const unique = findUniqueCategoryBySearchQuery(categories, t);
    if (unique) {
      setFocusedCategoryId(unique.id);
      return;
    }

    const nameMatches = categories.filter((c) => categoryMatches(c, t));
    if (nameMatches.length === 1) {
      setFocusedCategoryId(nameMatches[0]!.id);
      return;
    }

    if (visibleCategories.length === 1) {
      setFocusedCategoryId(visibleCategories[0]!.id);
    }
  }, [categories, searchQuery, visibleCategories]);

  const handleViewAll = useCallback(
    (categoryId: string) => {
      const t = searchQuery.trim();
      navigate(
        `/home/category/${categoryId}${
          t ? `?q=${encodeURIComponent(t)}` : ""
        }`
      );
    },
    [navigate, searchQuery]
  );

  const handlePrimaryTagSearch = useCallback((label: string) => {
    setFocusedCategoryId(null);
    setSearchQuery(label);
  }, []);

  const handleContentScroll = useCallback(
    (e: React.UIEvent<HTMLDivElement>) => {
      setBannerScrolled(e.currentTarget.scrollTop > 8);
    },
    []
  );

  const trimmedSearch = searchQuery.trim();
  const showNoMatchingResults =
    trimmedSearch.length > 0 && categoriesForList.length === 0;
  const showNoCatalogResults =
    !loading && trimmedSearch.length === 0 && categoriesForList.length === 0;

  useEffect(() => {
    setBannerScrolled(false);
  }, [showNoMatchingResults, focusedCategoryId]);

  const isCategoryFocus = Boolean(focusedCategory && focusedCategoryId);

  const topBarClassName = [
    styles.topBar,
    isCategoryFocus ? styles.topBarFocused : "",
    bannerScrolled ? styles.topBarScrolled : "",
  ]
    .filter(Boolean)
    .join(" ");

  return (
    <div className={styles.shell} onScroll={handleContentScroll}>
      {isCategoryFocus ? (
        <header className={topBarClassName}>
          <button
            type="button"
            className={styles.backBtn}
            onClick={exitCategoryFocus}
            aria-label="Back to Browse all"
          >
            <BackChevronIcon />
          </button>
          <h1 className={styles.focusedCategoryTitle}>
            {focusedCategory?.name ?? "Category"}
          </h1>
          <div className={styles.headerSpacer} aria-hidden />
        </header>
      ) : (
        <header
          className="browseAllPageHeader"
        >
          <SearchHeader
            alwaysVisible
            syncToSelector=".main-content"
            searchTerm={searchQuery}
            onSearchChange={setSearchQuery}
            onSubmitSearch={submitSearchEnter}
          />
            
        </header>
      )}
      <div
        className={
          showNoMatchingResults || showNoCatalogResults || loading
            ? styles.contentEmpty
            : styles.content
        }
      >
        {loading ? (
          <div className={styles.emptyState} role="status">
            <h2 className={styles.emptyTitle}>Loading titles</h2>
            <p className={styles.emptySubtitle}>Fetching latest catalog...</p>
          </div>
        ) : showNoMatchingResults ? (
          <div className={styles.emptyState} role="status">
            <h2 className={styles.emptyTitle}>No matching results</h2>
            <p className={styles.emptySubtitle}>
              We couldn&apos;t find what you are looking for. Try different
              criteria.
            </p>
            <button
              type="button"
              className={styles.clearSearchAction}
              onClick={clearSearch}
            >
              Clear search
            </button>
          </div>
        ) : showNoCatalogResults ? (
          <div className={styles.emptyState} role="status">
            <h2 className={styles.emptyTitle}>No titles available yet</h2>
            <p className={styles.emptySubtitle}>
              {error || "We could not load published titles from the catalog right now."}
            </p>
          </div>
        ) : showNoMatchingResults ? (
          <EmptySearchState onClearSearch={clearSearch} />
        ) : (
          categoriesForList.map((category) => (
            <CategoryRow
              key={category.id}
              category={category}
              onViewAll={handleViewAll}
              onPrimaryTagSearch={handlePrimaryTagSearch}
              hideViewAll={isCategoryFocus}
            />
          ))
        )}
      </div>
    </div>
  );
}
